<script setup>
import { ref, computed, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { PricingService } from "@/services";
import { getItem } from "@/common/storage";
import StripeCheckoutModal from "@/components/payments/StripeCheckoutModal.vue";
import { useAuthModals } from "@/composables/useAuthModals";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  view: {
    type: String,
    default: "all", // 'all' | 'upgrade' | 'subscription' | 'payg'
  },
  requiredCredits: {
    type: Number,
    default: 0.5,
  },
  currentBalance: {
    type: Number,
    default: 0,
  },
  showRequiredCredits: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["close", "update:isOpen"]);
const toast = useToast();
const showRequiredCredits = computed(() => props.showRequiredCredits);

// 使用本地状态管理弹窗的开关
const localIsOpen = ref(props.isOpen);

const billingCycle = ref("yearly"); // 'monthly' or 'yearly'
const selectedCredits = ref(500); // 默认选择50积分
const selectedPlan = ref("personal"); // 'personal', 'professional', 'payAsYouGo'

// 计费周期选项
const billingCycleItems = [
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const store = useStore();
const token = computed(() => store.state.user.token);
const userInfo = computed(() => store.state.user.userInfo);
const subscribe = computed(() => store.state.common.subscribe);

const getUserInfo = (payload) => store.dispatch("user/getUserInfo", payload);

// 订阅类型等级: "Free", "Personal", "Professional"
const userSubscribe = computed(() => userInfo.value.product_name);
// 订阅周期等级: "Monthly", "Yearly"
const userSubscribeInterval = computed(() => userInfo.value.recurring_interval);

const buyLoading = ref(false);

const pendingCancel = computed(
  () => Number(userInfo.value?.pending_cancel || 0) === 1
);
const isPaidPlanUser = computed(
  () => Number(userInfo.value?.product_id || 0) > 0
);

const showSubscriptionPlans = computed(() => props.view !== "payg");
const showPayAsYouGo = computed(() => {
  if (props.view === "upgrade" || props.view === "subscription") {
    return false;
  }
  // 仅非 Free 且非 pending_cancel 用户显示 Pay As You Go
  if (pendingCancel.value) return false;
  return isPaidPlanUser.value;
});
const canBuy = computed(() => {
  if (props.view === "payg") return showPayAsYouGo.value;
  return true;
});

const selectedSubscriptionAction = computed(() => {
  if (selectedPlan.value !== "personal" && selectedPlan.value !== "professional") {
    return "buy";
  }
  if (!hasActiveSubscription.value) {
    return "buy";
  }
  return getPlanActionType(selectedPlan.value, billingCycle.value);
});

const modalTitle = computed(() => {
  if (showRequiredCredits.value) return "Not Enough Credits";
  if (selectedPlan.value === "payAsYouGo") return "Buy More Credits";
  if (selectedSubscriptionAction.value === "downgrade") {
    return "Downgrade Subscription";
  }
  if (selectedSubscriptionAction.value === "upgrade") {
    return "Upgrade Subscription";
  }
  return "Choose Your Plan";
});

const modalDescription = computed(() => {
  if (showRequiredCredits.value) return "";
  if (selectedPlan.value === "payAsYouGo") {
    return `Current balance: ${new Intl.NumberFormat().format(
      userInfo.value?.credits_sum || 0
    )} credits. Choose a package below to get more credits.`;
  }
  if (selectedSubscriptionAction.value === "downgrade") {
    return "Downgrade takes effect immediately. Your existing credits remain available.";
  }
  if (selectedSubscriptionAction.value === "upgrade") {
    return "Upgrade now to unlock more credits immediately.";
  }
  return `Current balance: ${new Intl.NumberFormat().format(
    userInfo.value?.credits_sum || 0
  )} credits. Choose a plan below.`;
});

const primaryButtonLabel = computed(() => {
  if (selectedPlan.value === "payAsYouGo") return "Buy Now";
  if (selectedSubscriptionAction.value === "downgrade") return "Downgrade";
  if (selectedSubscriptionAction.value === "upgrade") return "Upgrade";
  return "Buy Now";
});

const stripeCheckoutOpen = ref(false);
const stripeCheckoutUrl = ref("");

// 订阅等级映射
const subscriptionLevel = {
  Free: 0,
  Personal: 1,
  Professional: 2,
};

// 控制是否禁用低于当前等级的订阅选项
// 当前弹窗允许在付费套餐内做受限降级：Professional -> Personal（同周期）
const disableLowerPlans = ref(true);

const normalizePlanType = (value) => String(value || "").toLowerCase();
const normalizeCycleValue = (value) => {
  const raw = String(value || "").toLowerCase();
  if (raw === "month" || raw === "monthly") return "monthly";
  if (raw === "year" || raw === "yearly" || raw === "annual") {
    return "yearly";
  }
  return raw;
};

const getCurrentSubscriptionSnapshot = () => ({
  planType: normalizePlanType(userSubscribe.value || "Free"),
  billingCycle: normalizeCycleValue(userSubscribeInterval.value),
});

const isAllowedDowngradeTarget = (targetPlanType, targetCycle) => {
  const current = getCurrentSubscriptionSnapshot();
  return (
    current.planType === "professional" &&
    normalizePlanType(targetPlanType) === "personal" &&
    current.billingCycle !== "" &&
    current.billingCycle === normalizeCycleValue(targetCycle)
  );
};

const isUpgradeTarget = (targetPlanType, targetCycle) => {
  const current = getCurrentSubscriptionSnapshot();
  const currentPlanLevel = subscriptionLevel[userSubscribe.value || "Free"] || 0;
  const normalizedTargetPlan = normalizePlanType(targetPlanType);
  const normalizedTargetCycle = normalizeCycleValue(targetCycle);

  if (!current.billingCycle) {
    return false;
  }

  const targetKey =
    normalizedTargetPlan === "professional"
      ? "Professional"
      : normalizedTargetPlan === "personal"
        ? "Personal"
        : "Free";
  const targetPlanLevel = subscriptionLevel[targetKey] || 0;

  if (normalizedTargetPlan === current.planType) {
    return current.billingCycle === "monthly" && normalizedTargetCycle === "yearly";
  }

  return targetPlanLevel > currentPlanLevel;
};

const getPlanActionType = (targetPlanType, targetCycle) => {
  if (isAllowedDowngradeTarget(targetPlanType, targetCycle)) {
    return "downgrade";
  }
  if (isUpgradeTarget(targetPlanType, targetCycle)) {
    return "upgrade";
  }
  return "unsupported";
};

// 判断是否是当前订阅（需要禁用 - 类型1）
const isCurrentSubscription = computed(() => {
  return (planType, cycle) => {
    // 如果 pending_cancel 为1，视为 free 用户，所有套餐都可以选择
    if (pendingCancel.value) return false;

    const currentPlan = userSubscribe.value || "Free";
    const currentCycle = userSubscribeInterval.value || "";

    // 如果计划类型和周期都匹配，则是当前订阅
    return (
      planType.toLowerCase() === currentPlan.toLowerCase() &&
      cycle.toLowerCase() === currentCycle.toLowerCase()
    );
  };
});

// 判断是否低于当前订阅等级（需要禁用 - 类型2）
// 注意：后续如需取消此禁用逻辑，只需将 disableLowerPlans 改为 false
const isLowerThanCurrent = computed(() => {
  return (planType, cycle) => {
    // 如果 pending_cancel 为1，视为 free 用户，所有套餐都可以选择
    if (pendingCancel.value) return false;

    // 如果配置为不禁用低级计划，直接返回 false
    if (!disableLowerPlans.value) {
      return false;
    }

    const currentPlan = userSubscribe.value || "Free";
    const currentPlanLevel = subscriptionLevel[currentPlan] || 0;
    const targetPlanLevel = subscriptionLevel[planType] || 0;

    // 如果目标计划类型等级低于当前，则禁用
    if (targetPlanLevel < currentPlanLevel) {
      return !isAllowedDowngradeTarget(planType, cycle);
    }

    return false;
  };
});

const isUnsupportedPlan = computed(() => {
  return (planType, cycle) => {
    if (!hasActiveSubscription.value) return false;
    if (isCurrentSubscription.value(planType, cycle)) return false;
    return getPlanActionType(planType, cycle) === "unsupported";
  };
});

// 判断计划是否可用（不是当前订阅且不低于当前等级）
const isPlanAvailable = (planKey, planType, cycle) => {
  return (
    !isCurrentSubscription.value(planType, cycle) &&
    !isLowerThanCurrent.value(planType, cycle)
  );
};

// 获取第一个可用的计划
const getFirstAvailablePlan = () => {
  const currentCycle = billingCycle.value;

  // 按顺序检查：personal -> professional -> payAsYouGo
  if (isPlanAvailable("personal", "Personal", currentCycle)) {
    return "personal";
  }
  if (isPlanAvailable("professional", "Professional", currentCycle)) {
    return "professional";
  }
  // Pay As You Go 仅在付费用户且 pending_cancel 不为1时可用
  if (isPaidPlanUser.value && !pendingCancel.value) {
    return "payAsYouGo";
  }

  // 如果都没有可用的，返回 null（这种情况理论上不应该发生）
  return "personal";
};

const getFirstAvailableSubscriptionPlan = () => {
  const currentCycle = billingCycle.value;
  if (isPlanAvailable("personal", "Personal", currentCycle)) {
    return "personal";
  }
  if (isPlanAvailable("professional", "Professional", currentCycle)) {
    return "professional";
  }
  return "personal";
};

const __PRO__ = __CUSTOM_ENV__ === "production";

// 检查用户是否已有订阅
const hasActiveSubscription = computed(() => {
  return (
    userInfo.value?.subscription_id &&
    userInfo.value?.product_id &&
    Number(userInfo.value.product_id) !== 0
  );
});

const downgradeSubscription = async (params) => {
  if (!hasActiveSubscription.value) {
    return;
  }

  try {
    const downgradeParams = {
      subscription_id: userInfo.value.subscription_id,
      user_id: userInfo.value.user_info_id,
      current_product_id: userInfo.value.product_id,
      product_id: params.productId,
      price_id: params.priceId,
    };

    const result = await PricingService.downgradeSubscribe(
      userInfo.value.user_info_id,
      downgradeParams
    );

    if (result?.status === 200) {
      await getUserInfo({ user_id: userInfo.value.user_info_id });
      buyLoading.value = false;
      localIsOpen.value = false;
      toast.add({
        title: "Success",
        description:
          "Subscription downgraded successfully. Your existing credits remain available.",
        color: "success",
        icon: "i-lucide-check-circle",
      });
    } else {
      throw new Error(result?.message || "降级订阅失败");
    }
  } catch (error) {
    buyLoading.value = false;
    toast.add({
      title: "Error",
      description:
        error.message || "Failed to downgrade subscription. Please try again.",
      color: "error",
      icon: "i-lucide-x-circle",
    });
  }
};

// 更新订阅（已有订阅时使用）
const updateSubscription = async (params) => {
  if (!hasActiveSubscription.value) {
    return;
  }

  try {
    const updateParams = {
      subscription_id: userInfo.value.subscription_id,
      user_id: userInfo.value.user_info_id,
      product_id: params.productId,
      price_id: params.priceId,
    };
    const result = await PricingService.updateSubscribe(
      userInfo.value.user_info_id,
      updateParams
    );

    if (result?.status === 200) {
      console.log("订阅更新成功, 准备调用 getUserInfo");
      await getUserInfo({ user_id: userInfo.value.user_info_id });
      buyLoading.value = false;
      localIsOpen.value = false;
      toast.add({
        title: "Success",
        description: "Subscription updated successfully!",
        color: "success",
        icon: "i-lucide-check-circle",
      });
    } else {
      throw new Error(result?.message || "更新订阅失败");
    }
  } catch (error) {
    console.error("更新订阅失败", error);
    buyLoading.value = false;
    toast.add({
      title: "Error",
      description:
        error.message || "Failed to update subscription. Please try again.",
      color: "error",
      icon: "i-lucide-x-circle",
    });
  }
};

const openStripeCheckout = (sessionData) => {
  stripeCheckoutUrl.value = sessionData?.checkout_url || "";
  if (!stripeCheckoutUrl.value) {
    throw new Error("Stripe checkout session data invalid");
  }
  stripeCheckoutOpen.value = true;
};

const STRIPE_RETURN_REFRESH_DELAY_MS = 1500;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const onStripeCheckoutCompleted = async () => {
  stripeCheckoutOpen.value = false;
  buyLoading.value = false;
  stripeCheckoutUrl.value = "";
  await sleep(STRIPE_RETURN_REFRESH_DELAY_MS);
  await getUserInfo({ user_id: userInfo.value.user_info_id });
  localIsOpen.value = false;
  toast.add({
    title: "Success",
    description: "Payment successful.",
    color: "success",
    icon: "i-lucide-check-circle",
  });
};

const onStripeCheckoutFailed = (message) => {
  stripeCheckoutOpen.value = false;
  buyLoading.value = false;
  stripeCheckoutUrl.value = "";
  toast.add({
    title: "Error",
    description: message || "Failed to start Stripe checkout.",
    color: "error",
    icon: "i-lucide-x-circle",
  });
};

const { openSignInModal } = useAuthModals();

// 订阅支付（创建新订阅或更新订阅）
const onCallPayment = async (params) => {
  if (!token.value) {
    // 登录已弹窗化:旧 /sign-in 页已 301,改开登录弹窗(登录后回到定价页)。
    openSignInModal("/pricing/");
    return;
  }

  // 如果用户已有订阅，使用更新订阅 API
  if (hasActiveSubscription.value) {
    const actionType = getPlanActionType(params.planType, params.billingCycle);

    if (actionType === "downgrade") {
      await downgradeSubscription(params);
      return;
    }

    if (actionType === "upgrade") {
      await updateSubscription(params);
      return;
    }

    throw new Error("This subscription change is not supported.");
    return;
  }

  const result = await getUserInfo({ user_id: userInfo.value.user_info_id });

  if (result && result.status === 403) {
    store.commit("common/SET_SUBSCRIBE", {
      planType: params.planType,
      billingCycle: params.billingCycle,
    });
    return;
  }

  if (!params.priceId) {
    throw new Error("Stripe live price id is not configured.");
  }

  const sessionRes = await PricingService.createSubscriptionCheckoutSession(
    userInfo.value.user_info_id,
    {
      price_id: params.priceId,
      product_id: params.productId,
      current_url: (() => {
        const u = new URL(window.location.href);
        u.searchParams.set("stripe_checkout_consumer", "credits-modal");
        return u.toString();
      })(),
    }
  );
  if (sessionRes?.status !== 200) {
    throw new Error(sessionRes?.message || "Create checkout session failed");
  }
  openStripeCheckout(sessionRes.data);
};

// 自定义积分支付
const onConfirmBuy = async () => {
  const params = {
    amount: payAsYouGoPricing[selectedCredits.value].price,
    quantity: selectedCredits.value,
    charge_name: `${selectedCredits.value} credits additional`,
    current_url: (() => {
      const u = new URL(window.location.href);
      u.searchParams.set("stripe_checkout_consumer", "credits-modal");
      return u.toString();
    })(),
  };

  // 1) 优先尝试后端 off_session 直扣（用户无需再次输入支付信息）
  const directRes = await PricingService.createAddonDirectCharge(
    userInfo.value.user_info_id,
    params
  );
  if (directRes?.status !== 200) {
    throw new Error(directRes?.message || "Direct charge failed");
  }

  const flow = directRes?.data?.flow;
  if (flow === "direct_charge") {
    // 直扣成功：立即刷新余额并关闭弹窗
    await getUserInfo({ user_id: userInfo.value.user_info_id });
    buyLoading.value = false;
    localIsOpen.value = false;
    toast.add({
      title: "Success",
      description: "Payment successful.",
      color: "success",
      icon: "i-lucide-check-circle",
    });
    return;
  }

  // 2) 无可复用支付方式/需认证等场景，回退到嵌入式 Checkout
  const checkoutRes = await PricingService.createAddonCheckoutSession(
    userInfo.value.user_info_id,
    params
  );
  if (checkoutRes?.status !== 200) {
    throw new Error(checkoutRes?.message || "Create checkout session failed");
  }
  openStripeCheckout(checkoutRes.data);
};

// 初始化函数，在弹窗打开时执行
const initModal = async () => {
  const sub = subscribe.value || getItem("subscribe") || null;
  if (!sub || !token.value) return;

  const planType = sub.planType;
  const billingCycleValue = sub.billingCycle;
  if (!planType || !billingCycleValue) {
    store.commit("common/SET_SUBSCRIBE", null);
    return;
  }
  store.commit("common/SET_SUBSCRIBE", null);
  await onCallPayment({
    planType,
    billingCycle: billingCycleValue,
    productId:
      pricingPlans.value[planType]?.localProductId?.[billingCycleValue],
    priceId: pricingPlans.value[planType]?.priceId?.[billingCycleValue],
  });
};

// 监听 props.isOpen 的变化，同步到本地状态
watch(
  () => props.isOpen,
  (newValue) => {
    localIsOpen.value = newValue;
  }
);

// 监听本地状态的变化，同步更新全局状态
watch(localIsOpen, (newValue) => {
  emit("update:isOpen", newValue);
  if (!newValue) {
    emit("close");
  }
});

// 监听弹窗打开状态
watch(
  localIsOpen,
  (newValue) => {
    if (newValue) {
      console.log("弹窗打开");
      initModal();
      // 弹窗打开时，根据入口选择默认计划
      if (props.view === "payg") {
        // 如果 pending_cancel 为1，不能选择 payAsYouGo，选择其他可用计划
        if (pendingCancel.value) {
          selectedPlan.value = getFirstAvailableSubscriptionPlan();
        } else {
          selectedPlan.value = "payAsYouGo";
        }
      } else if (props.view === "upgrade" || props.view === "subscription") {
        selectedPlan.value = getFirstAvailableSubscriptionPlan();
      } else {
        selectedPlan.value = getFirstAvailablePlan();
      }

      if (
        (props.view === "upgrade" || props.view === "subscription") &&
        hasActiveSubscription.value &&
        normalizePlanType(userSubscribe.value) === "professional"
      ) {
        selectedPlan.value = "personal";
      }
    }
  },
  { immediate: true }
); // immediate: true 确保组件首次挂载时也执行

watch(
  () => props.view,
  (newView) => {
    if (!localIsOpen.value) return;
    if (newView === "payg") {
      // 如果 pending_cancel 为1，不能选择 payAsYouGo，选择其他可用计划
      if (pendingCancel.value) {
        selectedPlan.value = getFirstAvailableSubscriptionPlan();
      } else {
        selectedPlan.value = "payAsYouGo";
      }
      return;
    }
    if (newView === "upgrade" || newView === "subscription") {
      selectedPlan.value = getFirstAvailableSubscriptionPlan();
      if (
        hasActiveSubscription.value &&
        normalizePlanType(userSubscribe.value) === "professional"
      ) {
        selectedPlan.value = "personal";
      }
      return;
    }
    selectedPlan.value = getFirstAvailablePlan();
  }
);

// 监听计费周期变化，自动选择第一个可用计划
watch(billingCycle, () => {
  if (!showSubscriptionPlans.value) return;
  const availablePlan = getFirstAvailablePlan();
  // 如果当前选中的计划不可用，则切换到第一个可用计划
  if (selectedPlan.value === "personal") {
    if (!isPlanAvailable("personal", "Personal", billingCycle.value)) {
      selectedPlan.value = availablePlan;
    }
  } else if (selectedPlan.value === "professional") {
    if (!isPlanAvailable("professional", "Professional", billingCycle.value)) {
      selectedPlan.value = availablePlan;
    }
  }
  // payAsYouGo 不受计费周期影响，不需要检查
});

// 监听用户订阅状态变化，自动选择第一个可用计划
watch(
  [userSubscribe, userSubscribeInterval],
  () => {
    if (!showSubscriptionPlans.value) return;
    const availablePlan = getFirstAvailablePlan();
    // 如果当前选中的计划不可用，则切换到第一个可用计划
    if (selectedPlan.value === "personal") {
      if (!isPlanAvailable("personal", "Personal", billingCycle.value)) {
        selectedPlan.value = availablePlan;
      }
    } else if (selectedPlan.value === "professional") {
      if (
        !isPlanAvailable("professional", "Professional", billingCycle.value)
      ) {
        selectedPlan.value = availablePlan;
      }
    }
    // payAsYouGo 仅在付费用户时显示，如果用户订阅状态变化可能影响显示
    if (!isPaidPlanUser.value && selectedPlan.value === "payAsYouGo") {
      selectedPlan.value = availablePlan;
    }
    // 如果 pending_cancel 为1，且当前选中的是 payAsYouGo，切换到其他可用计划
    if (pendingCancel.value && selectedPlan.value === "payAsYouGo") {
      selectedPlan.value = availablePlan;
    }
  },
  { deep: true }
);

// 监听 pendingCancel 变化，如果变为 true 且当前选中的是 payAsYouGo，切换到其他可用计划
watch(pendingCancel, (newValue) => {
  if (!localIsOpen.value) return;
  if (newValue && selectedPlan.value === "payAsYouGo") {
    selectedPlan.value = getFirstAvailableSubscriptionPlan();
  }
});

watch(stripeCheckoutOpen, (open) => {
  if (!open) {
    // 用户关闭支付弹窗或支付流程结束时，确保按钮停止 loading
    buyLoading.value = false;
  }
});

// 销毁
onBeforeUnmount(() => {
  stripeCheckoutOpen.value = false;
});

// Pay As You Go 价格表
const payAsYouGoPricing = {
  500: { price: 15.0, pricePerCredit: 0.03 },
  1000: { price: 25.0, pricePerCredit: 0.025 },
  2000: { price: 40.0, pricePerCredit: 0.02 },
  5000: { price: 75.0, pricePerCredit: 0.015 },
  10000: { price: 150.0, pricePerCredit: 0.015 },
  20000: { price: 200.0, pricePerCredit: 0.01 },
};

// Pay As You Go 积分选项
const creditOptions = [
  { value: 500, label: "500 Credits" },
  { value: 1000, label: "1,000 Credits" },
  { value: 2000, label: "2,000 Credits" },
  { value: 5000, label: "5,000 Credits" },
  { value: 10000, label: "10,000 Credits" },
  { value: 20000, label: "20,000 Credits" },
];

const pricingPlans = computed(() => ({
  personal: {
    name: "Personal",
    credits: "500 Credits/month",
    pricePerCredit:
      billingCycle.value === "monthly" ? "$0.020/credit" : "$0.018/credit",
    originalPrice:
      billingCycle.value === "monthly" ? "US$10.00/mo" : "US$10.00/mo",
    price: billingCycle.value === "monthly" ? "10.00" : "108.00",
    currency: billingCycle.value === "monthly" ? "10.00" : "9.00",
    description: "Automatically renew, or cancel anytime.",
    localProductId: {
      monthly: 804215,
      yearly: 804216,
    },
    priceId: {
      monthly: !__PRO__
        ? "price_1TMIHcLb6N7Fd5AC4VbPczbw"
        : "price_1TMjrRLOAzmVXp5dAv56319V",
      yearly: !__PRO__
        ? "price_1TMIOwLb6N7Fd5ACosL8BaFL"
        : "price_1TMjrRLOAzmVXp5d6jZo7Vzc",
    },
  },
  professional: {
    name: "Professional",
    credits: "2,000 Credits/month",
    pricePerCredit:
      billingCycle.value === "monthly" ? "$0.015/credit" : "$0.0135/credit",
    originalPrice:
      billingCycle.value === "monthly" ? "US$10.00/mo" : "US$30.00/mo",
    price: billingCycle.value === "monthly" ? "30.00" : "324.00",
    currency: billingCycle.value === "monthly" ? "30.00" : "27.00",
    description: "Automatically renew, or cancel anytime.",

    localProductId: {
      monthly: 804217,
      yearly: 804218,
    },
    priceId: {
      monthly: !__PRO__
        ? "price_1TMIR2Lb6N7Fd5ACUJIXG2ZI"
        : "price_1TMjrtLOAzmVXp5d3dTxlNsT",
      yearly: !__PRO__
        ? "price_1TMITsLb6N7Fd5AC65xdsZGe"
        : "price_1TMjtqLOAzmVXp5dT7ov6o7a",
    },
  },
}));

// 计算 Pay As You Go 的动态价格
const payAsYouGoPrice = computed(() => {
  const pricing = payAsYouGoPricing[selectedCredits.value];
  return {
    name: "Pay As You Go",
    credits: `${selectedCredits.value} Credits`,
    pricePerCredit: `$${pricing.pricePerCredit.toFixed(3)}/Credit`,
    price: pricing.price.toFixed(2),
    currency: `US$ ${pricing.price.toFixed(2)}`,
    description: "Credits are permanent.",
  };
});

// 购买
const handleBuyNow = async () => {
  // 检查当前选中的计划是否可用
  if (selectedPlan.value === "personal") {
    if (
      !isPlanAvailable("personal", "Personal", billingCycle.value) ||
      isUnsupportedPlan.value("Personal", billingCycle.value)
    ) {
      toast.add({
        title: "Error",
        description: "This plan is not available for purchase.",
        color: "error",
        icon: "i-lucide-x-circle",
      });
      return;
    }
  } else if (selectedPlan.value === "professional") {
    if (
      !isPlanAvailable("professional", "Professional", billingCycle.value) ||
      isUnsupportedPlan.value("Professional", billingCycle.value)
    ) {
      toast.add({
        title: "Error",
        description: "This plan is not available for purchase.",
        color: "error",
        icon: "i-lucide-x-circle",
      });
      return;
    }
  }
  // payAsYouGo 检查：如果 pending_cancel 为1，不能购买
  if (selectedPlan.value === "payAsYouGo") {
    if (pendingCancel.value) {
      toast.add({
        title: "Error",
        description:
          "Pay As You Go is not available when subscription is pending cancellation.",
        color: "error",
        icon: "i-lucide-x-circle",
      });
      return;
    }
  }

  buyLoading.value = true;
  let selectedConfig = {};

  if (selectedPlan.value === "personal") {
    selectedConfig = {
      planType: "personal",
      billingCycle: billingCycle.value,
      productId: pricingPlans.value.personal.localProductId[billingCycle.value],
      priceId: pricingPlans.value.personal.priceId[billingCycle.value],
      isSubscription: true,
    };
  } else if (selectedPlan.value === "professional") {
    selectedConfig = {
      planType: "professional",
      billingCycle: billingCycle.value,
      productId:
        pricingPlans.value.professional.localProductId[billingCycle.value],
      priceId: pricingPlans.value.professional.priceId[billingCycle.value],
      isSubscription: true,
    };
  } else if (selectedPlan.value === "payAsYouGo") {
    const pricing = payAsYouGoPricing[selectedCredits.value];
    selectedConfig = {
      product: pricing.price,
      isSubscription: false,
    };
  }

  console.log("选择的配置:", selectedConfig);

  try {
    if (selectedConfig.isSubscription) {
      await onCallPayment(selectedConfig);
    } else {
      await onConfirmBuy();
    }
  } catch (error) {
    console.error("购买失败", error);
    buyLoading.value = false;
  }
};
</script>

<template>
  <UModal
    v-model:open="localIsOpen"
    :dismissible="false"
    :ui="{
      content:
        'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-2xl max-h-[90vh] rounded-lg overflow-y-auto',
    }"
  >
    <template #default>
      <span class="hidden"></span>
    </template>

    <template #content>
      <UButton
        color="neutral"
        variant="ghost"
        icon="i-lucide-x"
        aria-label="Close modal"
        @click="localIsOpen = false"
        class="absolute top-4 right-4 z-10 text-white hover:text-white/80"
      />
      <div class="text-white p-6 sm:p-8">
        <!-- Header with Icon -->
        <div class="text-center mb-6">
          <div
            class="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
          >
            <UIcon
              :name="
                showRequiredCredits
                  ? 'i-lucide-layers'
                  : 'material-symbols-light:diamond-shine-outline-rounded'
              "
              class="w-8 h-8 text-white"
            />
          </div>
          <h2 class="text-2xl sm:text-3xl font-bold mb-3 text-white">
            {{ modalTitle }}
          </h2>
          <p
            v-if="showRequiredCredits"
            class="text-sm sm:text-base text-slate-300"
          >
            You're out of credits! This action requires
            <span class="text-[#6E28FD] font-semibold"
              >{{ requiredCredits }} credits</span
            >
            (balance:
            <span class="text-[#6E28FD] font-semibold">{{
              new Intl.NumberFormat()
                .format(userInfo.credits_sum)
                .toLocaleString()
            }}</span
            >).
          </p>
          <p v-else class="text-sm sm:text-base text-slate-300">
            {{ modalDescription }}
          </p>
        </div>

        <!-- Billing Cycle Toggle -->
        <div class="flex justify-center mb-6" v-if="showSubscriptionPlans">
          <UTabs
            v-model="billingCycle"
            :items="billingCycleItems"
            :content="false"
            size="sm"
            color="primary"
            variant="pill"
            class="w-auto"
            :ui="{
              trigger:
                'group inline-flex items-center gap-1.5 transition-colors data-[state=active]:text-white',
            }"
          />
        </div>

        <!-- Pricing Cards -->
        <div class="flex flex-col gap-3 mb-4" v-if="showSubscriptionPlans">
          <!-- Personal Plan -->
          <div
            @click="
            !isCurrentSubscription('Personal', billingCycle) &&
              !isLowerThanCurrent('Personal', billingCycle) &&
              !isUnsupportedPlan('Personal', billingCycle)
                ? (selectedPlan = 'personal')
                : null
            "
            :class="[
              ' border rounded-xl p-4 transition-all relative',
              isCurrentSubscription('Personal', billingCycle) ||
              isLowerThanCurrent('Personal', billingCycle) ||
              isUnsupportedPlan('Personal', billingCycle)
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer',
              selectedPlan === 'personal'
                ? 'border-[#6E28FD] '
                : 'border-white/10 hover:border-[#6E28FD]/30',
            ]"
          >
            <!-- 当前订阅标签 -->
            <div
              v-if="isCurrentSubscription('Personal', billingCycle)"
              class="absolute top-2 right-2 bg-[#6E28FD] text-white text-xs px-2 py-1 rounded-full"
            >
              Current Subscriptions
            </div>
            <!-- 低级订阅标签 -->
            <div
              v-else-if="isLowerThanCurrent('Personal', billingCycle)"
              class="absolute top-2 right-2 bg-slate-600 text-white text-xs px-2 py-1 rounded-full"
            >
              Downgrade available
            </div>
            <div
              v-else-if="isUnsupportedPlan('Personal', billingCycle)"
              class="absolute top-2 right-2 bg-slate-600 text-white text-xs px-2 py-1 rounded-full"
            >
              Unavailable
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3 flex-1">
                <div class="flex-1">
                  <h3
                    class="text-lg font-semibold mb-1"
                    :class="
                      selectedPlan === 'personal'
                        ? 'text-[#6E28FD]'
                        : 'text-white'
                    "
                  >
                    {{ pricingPlans.personal.name }}
                  </h3>
                  <p class="text-sm text-slate-400">
                    {{ pricingPlans.personal.credits }},
                    {{ pricingPlans.personal.pricePerCredit }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    {{ pricingPlans.personal.description }}
                  </p>
                </div>
              </div>
              <div class="flex flex-col items-end text-right">
                <div
                  class="flex flex-col sm:flex-row items-start sm:items-center gap-0 sm:gap-2"
                >
                  <div class="flex justify-end w-full">
                    <p
                      v-if="billingCycle === 'yearly'"
                      class="text-xs text-slate-500 line-through mb-1 sm:mb-0 sm:mr-2 text-right"
                    >
                      {{ pricingPlans.personal.originalPrice }}
                    </p>
                  </div>
                  <div class="flex flex-row items-end">
                    <p class="text-l font-bold text-[#6E28FD]">US$</p>
                    <p class="text-3xl font-bold text-[#6E28FD]">
                      {{ pricingPlans.personal.currency }}/mo
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Professional Plan -->
          <div
            @click="
            !isCurrentSubscription('Professional', billingCycle) &&
              !isLowerThanCurrent('Professional', billingCycle) &&
              !isUnsupportedPlan('Professional', billingCycle)
                ? (selectedPlan = 'professional')
                : null
            "
            :class="[
              ' border rounded-xl p-4 transition-all cursor-pointer relative',
              isCurrentSubscription('Professional', billingCycle) ||
              isLowerThanCurrent('Professional', billingCycle) ||
              isUnsupportedPlan('Professional', billingCycle)
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer',
              selectedPlan === 'professional'
                ? 'border-[#6E28FD] '
                : 'border-white/10 hover:border-[#6E28FD]/30',
            ]"
          >
            <!-- 当前订阅标签 -->
            <div
              v-if="isCurrentSubscription('Professional', billingCycle)"
              class="absolute top-2 right-2 bg-[#6E28FD] text-white text-xs px-2 py-1 rounded-full"
            >
              Current Subscriptions
            </div>
            <!-- 低级订阅标签 -->
            <div
              v-else-if="isLowerThanCurrent('Professional', billingCycle)"
              class="absolute top-2 right-2 bg-slate-600 text-white text-xs px-2 py-1 rounded-full"
            >
              Lower than current level
            </div>
            <div
              v-else-if="isUnsupportedPlan('Professional', billingCycle)"
              class="absolute top-2 right-2 bg-slate-600 text-white text-xs px-2 py-1 rounded-full"
            >
              Unavailable
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3 flex-1">
                <div class="flex-1">
                  <h3
                    class="text-lg font-semibold mb-1"
                    :class="
                      selectedPlan === 'professional'
                        ? 'text-[#6E28FD]'
                        : 'text-white'
                    "
                  >
                    {{ pricingPlans.professional.name }}
                  </h3>
                  <p class="text-sm text-slate-400">
                    {{ pricingPlans.professional.credits }},
                    {{ pricingPlans.professional.pricePerCredit }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    {{ pricingPlans.professional.description }}
                  </p>
                </div>
              </div>
              <div class="flex flex-col items-end text-right">
                <div
                  class="flex flex-col sm:flex-row items-start sm:items-center gap-0 sm:gap-2"
                >
                  <div class="flex justify-end w-full">
                    <p
                      v-if="billingCycle === 'yearly'"
                      class="text-xs text-slate-500 line-through mb-1 sm:mb-0 sm:mr-2 text-right"
                    >
                      {{ pricingPlans.professional.originalPrice }}
                    </p>
                  </div>
                  <div class="flex flex-row items-end">
                    <p class="text-l font-bold text-[#6E28FD]">US$</p>
                    <p class="text-3xl font-bold text-[#6E28FD]">
                      {{ pricingPlans.professional.currency }}/mo
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Or Divider -->
        <USeparator
          label="or"
          class="my-4"
          v-if="showSubscriptionPlans && showPayAsYouGo"
        />

        <!-- Pay As You Go -->
        <div
          @click="selectedPlan = 'payAsYouGo'"
          :class="[
            ' border rounded-xl p-4 mb-6 transition-all cursor-pointer',
            selectedPlan === 'payAsYouGo'
              ? 'border-[#6E28FD] '
              : 'border-white/10 hover:border-[#6E28FD]/30',
          ]"
          v-if="showPayAsYouGo"
        >
          <div class="flex items-center gap-3 mb-3">
            <h3
              class="text-lg font-semibold"
              :class="
                selectedPlan === 'payAsYouGo' ? 'text-[#6E28FD]' : 'text-white'
              "
            >
              {{ payAsYouGoPrice.name }}
            </h3>
          </div>
          <div
            class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          >
            <div class="flex items-center gap-3 w-full sm:w-auto">
              <USelectMenu
                v-model="selectedCredits"
                :items="creditOptions"
                value-attribute="value"
                option-attribute="label"
                class="w-40"
                value-key="value"
              />
              <span class="text-sm text-slate-400">{{
                payAsYouGoPrice.pricePerCredit
              }}</span>
            </div>
            <div class="text-left sm:text-right w-full sm:w-auto">
              <p class="text-2xl font-bold text-[#6E28FD]">
                {{ payAsYouGoPrice.currency }}
              </p>
            </div>
          </div>
          <p class="text-xs text-slate-500 mt-3">
            {{ payAsYouGoPrice.description }}
          </p>
        </div>

        <div
          v-if="props.view === 'payg' && !showPayAsYouGo"
          class="mb-6 border border-white/10 rounded-xl p-4 text-sm text-white/70"
        >
          Pay As You Go is available for paid plans.
        </div>

        <!-- Buy Now Button -->
        <UButton
          color="primary"
          size="xl"
          block
          icon="i-lucide-shopping-cart"
          @click="handleBuyNow"
          :loading="buyLoading"
          :disabled="!canBuy"
          class="mb-4 bg-linear-to-r from-[#6E28FD] to-purple-500 hover:shadow-lg hover:shadow-[#6E28FD]/40 transition-all text-white"
        >
          {{ primaryButtonLabel }}
        </UButton>

        <!-- Money Back Guarantee -->
        <div
          class="text-center text-sm text-slate-400 flex items-center justify-center gap-2"
        >
          <UIcon name="i-lucide-badge-check" class="w-4 h-4 text-green-500" />
          7 Days Money Back Guarantee
        </div>
      </div>
    </template>
  </UModal>
  <StripeCheckoutModal
    v-model:open="stripeCheckoutOpen"
    :checkout-url="stripeCheckoutUrl"
    consumer-key="credits-modal"
    @completed="onStripeCheckoutCompleted"
    @failed="onStripeCheckoutFailed"
  />
</template>
